import type { FC } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import Box from "@mui/material/Box";
import { DeleteUserErrorCodes, type UsersByOrganisationId } from "../../../api/users.types";
import { hasErrorCode } from "../../../api/errors";
import { useDeleteUser } from "../../../api/users";
import { ActionMenuDeleteMenuItem } from "../../../shared/ActionMenu";
import { usePermission } from "../../../shared/permissions";

type DeleteMenuItemProps = {
  user: UsersByOrganisationId["users"][number];
  onClose: () => void;
  onDeleted: () => void;
};

export const DeleteMenuItem: FC<DeleteMenuItemProps> = ({ user, onClose, onDeleted }) => {
  const { t } = useTranslation();
  const { mutateAsync, error } = useDeleteUser();
  const { hasPermission } = usePermission();

  const handleDelete = () => mutateAsync(user.userId, { onSuccess: onDeleted });

  return (
    <ActionMenuDeleteMenuItem
      missingPermission={!hasPermission("organization-api:user:delete")}
      confirmDeleteDialog={{
        header: t("User.DeleteConfirmation.Title"),
        message: (
          <Translate
            translationKey="User.DeleteConfirmation.Message"
            values={{
              email: (
                <Box component="span" sx={{ fontWeight: "bold" }}>
                  {user.email}
                </Box>
              ),
            }}
          />
        ),
        delete: t("User.DeleteConfirmation.Submit"),
        errorMessage: hasErrorCode(error, DeleteUserErrorCodes.hasConnections)
          ? t("UserSelect.RemoveUserConnections")
          : undefined,
      }}
      onClose={onClose}
      onDelete={handleDelete}
    />
  );
};
